import { useCallback, useState } from "react";
import { addDaysUtc, todayUtc } from "./format";

const DEFAULT_DAYS = 14;

/**
 * Each tab keeps its own range across tab switches, so three ranges can be compared side by side
 * without re-picking one every time. Session-scoped: a reload starts fresh.
 */
const keyFor = (tab: string): string => `dashboard.range.${tab}`;

const defaultRange = (): { from: string; to: string } => ({
    from: addDaysUtc(todayUtc(), -(DEFAULT_DAYS - 1)),
    to: todayUtc(),
});

const read = (tab: string): { from: string; to: string } | null => {
    try {
        const raw = sessionStorage.getItem(keyFor(tab));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed?.from === "string" && typeof parsed?.to === "string" ? parsed : null;
    } catch {
        return null;
    }
};

const write = (tab: string, from: string, to: string): void => {
    try {
        sessionStorage.setItem(keyFor(tab), JSON.stringify({ from, to }));
    } catch {
        // A blocked or full sessionStorage costs stickiness, not the range itself.
    }
};

/** For Explorer, whose range lives in the URL so a drill-through link stays shareable. */
export const readStickyRange = (tab: string): { from: string; to: string } =>
    read(tab) ?? defaultRange();

export const writeStickyRange = write;

export const useStickyRange = (
    tab: string
): [string, string, (from: string, to: string) => void] => {
    const [range, setRange] = useState(() => read(tab) ?? defaultRange());

    const apply = useCallback(
        (from: string, to: string) => {
            setRange({ from, to });
            write(tab, from, to);
        },
        [tab]
    );

    return [range.from, range.to, apply];
};
